import { Extension } from '@tiptap/core';
import { textblockTypeInputRule } from '@tiptap/core';

export interface CustomTypographyOptions {
  /**
   * Quotes to use for smart quotes (emDash, ellipsis, etc.)
   */
  leftDoubleQuote: string;
  rightDoubleQuote: string;
  leftSingleQuote: string;
  rightSingleQuote: string;
  emDash: string;
  ellipsis: string;
}

/**
 * Custom Typography extension that handles markdown-style shortcuts
 * but delegates h1 (#) to the SingleH1Extension.
 *
 * This extension handles:
 * - ## for h2
 * - ### for h3
 * - #### for h4
 * - Smart quotes, em dashes, ellipsis, etc.
 *
 * It does NOT handle # for h1 - that's handled by the title extension.
 */
export const CustomTypography = Extension.create<CustomTypographyOptions>({
  name: 'customTypography',

  addOptions() {
    return {
      leftDoubleQuote: '\u201C',
      rightDoubleQuote: '\u201D',
      leftSingleQuote: '\u2018',
      rightSingleQuote: '\u2019',
      emDash: '\u2014',
      ellipsis: '\u2026',
    };
  },

  addInputRules() {
    const headingType = this.editor.schema.nodes['heading'];

    if (!headingType) {
      return [];
    }

    return [
      // Heading 2: ##
      textblockTypeInputRule({
        find: /^#\s$/,
        type: headingType,
        getAttributes: () => ({ level: 2 }),
      }),
      // Heading 3: ###
      textblockTypeInputRule({
        find: /^##\s$/,
        type: headingType,
        getAttributes: () => ({ level: 3 }),
      }),
      // Heading 4: ####
      textblockTypeInputRule({
        find: /^###\s$/,
        type: headingType,
        getAttributes: () => ({ level: 4 }),
      }),
    ];
  },

  addProseMirrorPlugins() {
    return [];
  },
});

/**
 * Title extension that handles # markdown shortcut.
 * Since DocumentTitle ensures there's always an H1 as the first node,
 * this extension always converts `#` to H2.
 */
export const TitleExtension = Extension.create({
  name: 'title',

  addInputRules() {
    const headingType = this.editor.schema.nodes['heading'];

    if (!headingType) {
      return [];
    }

    return [
      // Heading shortcut: # → H2
      // Always creates H2 since DocumentTitle ensures H1 always exists
      textblockTypeInputRule({
        find: /^#\s$/,
        type: headingType,
        getAttributes: () => ({ level: 2 }),
      }),
    ];
  },
});
